import SntButton from "@/cleanup/widgets/forms/SntButton";
import { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { ButtonGroup, Dropdown } from "react-bootstrap";
import { useHistory } from "react-router-dom";

interface ToolbarItem {
  id: string;
  title: string;
  icon?: React.ReactNode;
  link?: string;
  fullLinkInNewTab?: string;
  customActiveClass?: string;
  isHideText?: boolean;
}

type ToolbarSection = ToolbarItem[];

interface ToolbarProps {
  handleClick?: (id: string) => void;
  selectedId?: string;
  sections?: ToolbarSection[];
  fixedSections?: ToolbarSection[];
  position?: "start" | "end";
  disableOverflow?: boolean; // When true, show all buttons without responsive overflow
}

const Toolbar: React.FC<ToolbarProps> = ({
  handleClick = () => {},
  selectedId,
  sections = [
    [
      {
        id: "LIST",
        title: "list",
        icon: "",
        link: "",
      },
    ],
  ],
  fixedSections,
  position = "end",
  disableOverflow = false,
}) => {
  const history = useHistory();

  const tabWidget = useRef<HTMLDivElement>(null);
  const fixedWidget = useRef<HTMLDivElement>(null);
  const container = useRef<HTMLDivElement>(null);
  const [hiddenItems, setHiddenItems] = useState<ToolbarSection[]>([]);
  // Initialize visibleItems with sections to avoid flash of empty content
  const [visibleItems, setVisibleItems] = useState<ToolbarSection[]>(sections);

  const [selectedItem, setSelectedItem] = useState<string | undefined>(selectedId);
  useEffect(() => {
    if (selectedId) {
      setSelectedItem(selectedId);
    }
  }, [selectedId]);

  const hiddenItemRender = useMemo(() => {
    return hiddenItems.map((section, key) => {
      return section.map((item, itemIdx) => (
        <Dropdown.Item
          key={itemIdx}
          className={`${
            item.id === selectedItem ? item.customActiveClass || "active" : ""
          }`}
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            if (item.fullLinkInNewTab) {
              window.open(item.fullLinkInNewTab);
            } else if (item.link) {
              history.push(item.link);
            } else {
              handleClick(item.id);
            }
            setSelectedItem(item.id);
          }}
        >
          {item.icon && <span className="me-2">{item.icon}</span>}
          {item.title}
        </Dropdown.Item>
      ));
    });
  }, [hiddenItems, handleClick, history, selectedItem]);

  const renderVisibleItem = (
    sectionsList: ToolbarSection[],
    selectedItem: string | undefined,
    handleClick: (id: string) => void,
    history: ReturnType<typeof useHistory>,
    setSelectedItem: React.Dispatch<React.SetStateAction<string | undefined>>,
    forceShowText: boolean = false
  ): React.ReactNode => {
    let forceHideText = false;
    if (!forceShowText && sectionsList && sectionsList.length > 1) {
      forceHideText = true;
    }
    return sectionsList.map((section, key) => {
      return (
        <div key={key} className="d-inline-block ms-2">
          <ButtonGroup>
            {section.map((item, itemIdx) => {
              return (
                <SntButton
                  key={itemIdx}
                  className={`${
                    item.id === selectedItem
                      ? item.customActiveClass || "active"
                      : ""
                  }`}
                  onClick={(e) => {
                    e.persist();
                    e.preventDefault();
                    e.stopPropagation();
                    if (item.fullLinkInNewTab) {
                      window.open(item.fullLinkInNewTab);
                    } else if (item.link) {
                      history.push(item.link);
                    } else {
                      handleClick(item.id);
                    }
                    setSelectedItem(item.id);
                  }}
                  icon={item.icon}
                  text={forceHideText ? "" : item.isHideText ? "" : item.title}
                  title={item.title}
                />
              );
            })}
          </ButtonGroup>
        </div>
      );
    });
  };

  const visibleItemRender = useMemo(() => {
    return renderVisibleItem(
      visibleItems,
      selectedItem,
      handleClick,
      history,
      setSelectedItem,
      disableOverflow
    );
  }, [visibleItems, handleClick, history, selectedItem, setSelectedItem, disableOverflow]);

  const fixedItemRender = useMemo(() => {
    if (!fixedSections || !fixedSections.length) return <></>;

    return renderVisibleItem(
      fixedSections,
      selectedItem,
      handleClick,
      history,
      setSelectedItem,
      disableOverflow
    );
  }, [fixedSections, handleClick, history, selectedItem, setSelectedItem, disableOverflow]);

  // Track if we need to recalculate (after sections change or resize)
  const needsRecalculation = useRef(true);

  // Calculate which items fit in the available space
  const calculateVisibleItems = () => {
    if (!needsRecalculation.current) return;

    // When overflow is disabled, show all items
    if (disableOverflow) {
      needsRecalculation.current = false;
      setVisibleItems(sections);
      setHiddenItems([]);
      return;
    }

    if (!tabWidget.current || !fixedWidget.current || !container.current) return;

    const tabWidgetChildren = Array.from(tabWidget.current.children);

    // If no children rendered yet, wait for next layout pass
    if (tabWidgetChildren.length === 0 && sections.length > 0) {
      return;
    }

    let currentWidth = 0;
    let visibleCount = 0;

    // Add width for fixed items
    const fixedWidgetChildren = Array.from(fixedWidget.current.children);
    for (let i = 0; i < fixedWidgetChildren.length; i++) {
      const child = fixedWidgetChildren[i] as HTMLElement;
      currentWidth += child.clientWidth + 8;
    }

    // Calculate how many sections fit
    for (let i = 0; i < tabWidgetChildren.length; i++) {
      const child = tabWidgetChildren[i] as HTMLElement;
      currentWidth += child.clientWidth + 8;
      if (container.current.clientWidth - currentWidth - 58 >= 0) {
        visibleCount++;
      } else {
        break;
      }
    }

    // Only update state if the count changed
    const newVisibleItems = sections.slice(0, visibleCount);
    const newHiddenItems = sections.slice(visibleCount);

    needsRecalculation.current = false;
    setVisibleItems(newVisibleItems);
    setHiddenItems(newHiddenItems);
  };

  // When sections change, show all items first so we can measure them
  useEffect(() => {
    needsRecalculation.current = true;
    setVisibleItems(sections);
    setHiddenItems([]);
  }, [sections]);

  // After DOM updates, calculate which items fit (runs synchronously before paint)
  useLayoutEffect(() => {
    calculateVisibleItems();
  });

  // Watch for container resize
  useEffect(() => {
    if (!container.current) return;

    const resizeObserver = new ResizeObserver(() => {
      // Trigger recalculation on next render
      needsRecalculation.current = true;
      setVisibleItems(sections);
      setHiddenItems([]);
    });

    resizeObserver.observe(container.current);
    return () => resizeObserver.disconnect();
  }, [sections]);

  return (
    <>
      {(sections.length > 0 || (fixedSections && fixedSections.length > 0)) && (
        <div ref={container} className="w-100">
          <div className={position === "start" ? "d-flex flex-wrap gap-2" : "d-flex flex-wrap gap-2 justify-content-end"}>
            <div className="d-inline-block" ref={tabWidget}>
              {visibleItemRender}
            </div>
            {hiddenItems.length > 0 && (
              <div className="d-inline-block">
                <Dropdown
                  className={`hide-arrow ms-1`}
                  style={{ zIndex: 1002 }}
                  as={ButtonGroup}
                >
                  <Dropdown.Toggle variant="sensolus-greylight">
                    ...
                  </Dropdown.Toggle>

                  <Dropdown.Menu>{hiddenItemRender}</Dropdown.Menu>
                </Dropdown>
              </div>
            )}
            <div className="d-inline-block" ref={fixedWidget}>
              {fixedItemRender}
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default Toolbar;
